import { ApplicationConfig, provideBrowserGlobalErrorListeners } from '@angular/core';
import { provideRouter, RouteReuseStrategy, ActivatedRouteSnapshot, DetachedRouteHandle, withRouterConfig } from '@angular/router';
import { routes } from './app.routes';
import { initializeApp } from 'firebase/app';
import { initializeAppCheck, ReCaptchaEnterpriseProvider } from 'firebase/app-check';
import { initializeFirestore, getFirestore } from 'firebase/firestore';
import { getAnalytics, isSupported as isAnalyticsSupported } from 'firebase/analytics';
import { HOME_HUB_CONFIG } from '@crewdle/home-hub/angular';
import { WHITE_LABEL_CONFIG } from '@crewdle/white-label/angular';
import { environment } from '../environments/environment';

// Initialize Firebase. `experimentalAutoDetectLongPolling` falls back from
// WebChannel/QUIC to long-polling when the transport is unhealthy — fixes the
// QUIC_NETWORK_IDLE_TIMEOUT / 400 storms that leave the build UI showing a
// stale `buildStatus.state` after the listener silently drops.
const app = initializeApp(environment.firebase);
// App Check (reCAPTCHA Enterprise) — attaches a bot-protection token so the
// backend can verify calls originate from this app. Skipped when no site key
// is set (staging). Initialized right after the app so tokens attach to reads.
if (environment.appCheckSiteKey) {
  initializeAppCheck(app, {
    provider: new ReCaptchaEnterpriseProvider(environment.appCheckSiteKey),
    isTokenAutoRefreshEnabled: true,
  });
}
initializeFirestore(app, { experimentalAutoDetectLongPolling: true });
// Firebase Analytics — feature-detect so SSR / non-secure contexts don't crash.
isAnalyticsSupported()
  .then((ok) => { if (ok) getAnalytics(app); })
  .catch(() => {});

class AppRouteReuseStrategy implements RouteReuseStrategy {
  shouldDetach(_route: ActivatedRouteSnapshot): boolean { return false; }
  store(_route: ActivatedRouteSnapshot, _handle: DetachedRouteHandle | null): void {}
  shouldAttach(_route: ActivatedRouteSnapshot): boolean { return false; }
  retrieve(_route: ActivatedRouteSnapshot): DetachedRouteHandle | null { return null; }

  shouldReuseRoute(future: ActivatedRouteSnapshot, curr: ActivatedRouteSnapshot): boolean {
    if (future.routeConfig?.path === 'project/:id') {
      return future.params['id'] === curr.params['id'];
    }
    return future.routeConfig === curr.routeConfig;
  }
}

export const appConfig: ApplicationConfig = {
  providers: [
    provideBrowserGlobalErrorListeners(),
    provideRouter(routes, withRouterConfig({ paramsInheritanceStrategy: 'always' })),
    { provide: RouteReuseStrategy, useClass: AppRouteReuseStrategy },
    {
      // Full-screen home hub (cross-product launcher; replaces the old
      // product-switcher dropdown).
      provide: HOME_HUB_CONFIG,
      useFactory: () => ({
        firestore: getFirestore(),
        domain: environment.production ? 'crewdle.ai' : 'stg.crewdle.ai',
      }),
    },
    {
      // Partner white-label branding (colors, logos, favicon, title).
      provide: WHITE_LABEL_CONFIG,
      useFactory: () => ({ firestore: getFirestore() }),
    },
  ],
};
